<template>
  <div id="glucose-readings">
    <div class="adm-toolbar">
      <div class="user-picker" ref="picker">
        <button
          type="button"
          class="ddl-trigger"
          :class="{ 'ddl-open': dropdownOpen, 'has-value': !!selectedEmail }"
          aria-haspopup="listbox"
          :aria-expanded="dropdownOpen ? 'true' : 'false'"
          @click="toggleDropdown"
        >
          <span class="ddl-value">
            {{ selectedEmail || "Select user…" }}
          </span>
          <span
            v-if="selectedEmail"
            class="ddl-clear"
            role="button"
            tabindex="0"
            aria-label="Clear user"
            @click.stop="clearUser"
            @keydown.enter.stop.prevent="clearUser"
            @keydown.space.stop.prevent="clearUser"
            @mousedown.prevent
          >
            <i class="fa fa-times" />
          </span>
          <i class="fa fa-chevron-down ddl-chevron" aria-hidden="true" />
        </button>
        <div v-if="dropdownOpen" class="ddl-panel" role="listbox">
          <div class="ddl-search">
            <i class="fa fa-search ddl-search-icon" aria-hidden="true" />
            <input
              ref="ddlSearch"
              v-model="userFilter"
              type="text"
              placeholder="Type email to filter…"
              class="ddl-search-input"
              @keydown.esc.prevent="closeDropdown"
              @keydown.enter.prevent="selectFirst"
            />
          </div>
          <div class="ddl-list">
            <div
              v-for="u in filteredUsers"
              :key="u.user_id"
              class="ddl-item"
              :class="{ selected: u.email === selectedEmail }"
              role="option"
              :aria-selected="u.email === selectedEmail ? 'true' : 'false'"
              @click="selectUser(u)"
            >
              <span class="ddl-item-email">{{ u.email }}</span>
              <span class="ddl-item-meta"
                >{{ u.reading_count }} reading{{
                  Number(u.reading_count) === 1 ? "" : "s"
                }}</span
              >
            </div>
            <div v-if="filteredUsers.length === 0" class="ddl-empty">
              No matches
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="selectedEmail && !isLoading && readings.length"
        class="adm-chip"
      >
        <strong>{{ readings.length.toLocaleString() }}</strong>
        <span class="adm-chip-label">readings</span>
      </div>
      <div class="adm-toolbar-spacer" />
    </div>

    <div v-if="listError" class="adm-error-banner" role="alert">
      <i class="fa fa-circle-exclamation" aria-hidden="true" />
      <span>{{ listError }}</span>
    </div>

    <div v-if="isLoading" class="adm-empty">
      <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
      <div class="adm-empty-sub">Loading readings…</div>
    </div>

    <div v-else-if="readings.length > 0" class="adm-card">
      <b-table
        id="readings-table"
        :items="readings"
        :fields="fields"
        hover
        small
        responsive
        :per-page="perPage"
        :current-page="currentPage"
        class="adm-table"
      />
      <div v-if="readings.length > perPage" class="adm-pagination">
        <b-pagination
          v-model="currentPage"
          :total-rows="readings.length"
          :per-page="perPage"
          first-number
          last-number
          aria-controls="readings-table"
        />
      </div>
    </div>

    <div class="adm-empty" v-else>
      <div class="adm-empty-icon"><i class="fa fa-tint" /></div>
      <div class="adm-empty-title">
        {{
          selectedEmail
            ? "No readings for this user"
            : "Select a user to view glucose readings"
        }}
      </div>
      <div class="adm-empty-sub">
        {{
          selectedEmail
            ? "Readings will appear here once the user logs one in the mobile app."
            : "Type or pick an email above to see their reported readings."
        }}
      </div>
    </div>
  </div>
</template>

<script>
import { customAxios } from "@/utils/customAxios";
import { apiErrorMessage } from "@/utils/apiError";

const pad = (n, w = 2) => String(n).padStart(w, "0");

function fmtEpoch(epoch) {
  if (epoch === null || epoch === undefined) return "";
  const d = new Date(Number(epoch) * 1000);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

export default {
  data() {
    return {
      isLoading: false,
      perPage: 20,
      currentPage: 1,
      listError: "",
      users: [],
      readings: [],
      selectedEmail: "",
      // searchable user dropdown
      dropdownOpen: false,
      userFilter: "",
      fields: [
        { key: "epoch", label: "Date / Time", sortable: true, formatter: fmtEpoch },
        {
          key: "value",
          label: "Value (mg/dL)",
          sortable: true,
          formatter: (v) =>
            v === null || v === undefined ? "" : Number(v).toFixed(1),
        },
        { key: "note", label: "Note", formatter: (v) => v || "—" },
      ],
    };
  },
  computed: {
    selectedUserId() {
      const u = this.users.find((x) => x.email === this.selectedEmail);
      return u ? u.user_id : null;
    },
    filteredUsers() {
      const q = (this.userFilter || "").trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter((u) =>
        (u.email || "").toLowerCase().includes(q)
      );
    },
  },
  watch: {
    selectedEmail() {
      this.currentPage = 1;
      const id = this.selectedUserId;
      if (id) {
        this.fetchReadings(id);
      } else {
        this.readings = [];
      }
    },
  },
  mounted() {
    this.fetchUsers();
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen ? this.closeDropdown() : this.openDropdown();
    },
    openDropdown() {
      this.dropdownOpen = true;
      this.userFilter = "";
      this.$nextTick(() => {
        if (this.$refs.ddlSearch) this.$refs.ddlSearch.focus();
      });
      document.addEventListener("mousedown", this.handleOutsideClick);
    },
    closeDropdown() {
      this.dropdownOpen = false;
      document.removeEventListener("mousedown", this.handleOutsideClick);
    },
    handleOutsideClick(e) {
      const root = this.$refs.picker;
      if (!root || !root.contains(e.target)) {
        this.closeDropdown();
      }
    },
    selectUser(u) {
      this.selectedEmail = u.email;
      this.closeDropdown();
    },
    clearUser() {
      this.selectedEmail = "";
      this.closeDropdown();
    },
    selectFirst() {
      if (this.filteredUsers.length > 0) {
        this.selectUser(this.filteredUsers[0]);
      }
    },
    fetchUsers() {
      customAxios
        .get("/admin/glucose-readings/users")
        .then((res) => {
          this.users = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
          this.listError = "";
        })
        .catch((err) => {
          console.error("Failed to load users", err);
          this.users = [];
          this.listError = apiErrorMessage(
            err,
            "Failed to load the list of users."
          );
        });
    },
    fetchReadings(userId) {
      this.isLoading = true;
      customAxios
        .get("/admin/glucose-readings", { params: { user_id: userId } })
        .then((res) => {
          this.readings = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
          this.listError = "";
        })
        .catch((err) => {
          console.error("Failed to load readings", err);
          this.readings = [];
          this.listError = apiErrorMessage(
            err,
            "Failed to load readings for this user."
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.loader {
  width: 40px;
  height: 40px;
  margin-bottom: 0.5rem;
}

/* ---------- searchable user dropdown ---------- */
.user-picker {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.ddl-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  height: 38px;
  padding: 0 0.6rem 0 0.85rem;
  background: #f7f8fb;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1f2540;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.ddl-trigger:hover {
  background: #ffffff;
  border-color: #d8dde9;
}
.ddl-trigger:focus,
.ddl-trigger.ddl-open {
  outline: none;
  background: #ffffff;
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.12);
}
.ddl-trigger:not(.has-value) .ddl-value {
  color: #98a0ad;
}
.ddl-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ddl-chevron {
  color: #98a0ad;
  font-size: 0.8rem;
  margin-left: 0.5rem;
  transition: transform 0.15s;
}
.ddl-trigger.ddl-open .ddl-chevron {
  transform: rotate(180deg);
}
.ddl-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 0.25rem;
  border-radius: 50%;
  color: #98a0ad;
  cursor: pointer;
}
.ddl-clear:hover {
  background: #eef0f4;
  color: #2a345d;
}
.ddl-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  background: white;
  border: 1px solid #e6e8ee;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  overflow: hidden;
}
.ddl-search {
  position: relative;
  padding: 0.5rem;
  border-bottom: 1px solid #eef0f4;
}
.ddl-search-icon {
  position: absolute;
  top: 50%;
  left: 1.05rem;
  transform: translateY(-50%);
  color: #98a0ad;
  font-size: 0.85rem;
  pointer-events: none;
}
.ddl-search-input {
  width: 100%;
  height: 32px;
  padding: 0 0.6rem 0 2rem;
  background: #f7f8fb;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.85rem;
}
.ddl-search-input:focus {
  outline: none;
  background: #ffffff;
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.1);
}
.ddl-list {
  max-height: 280px;
  overflow-y: auto;
}
.ddl-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  font-size: 0.88rem;
  color: #1f2540;
  cursor: pointer;
}
.ddl-item:hover {
  background: #f7f8fb;
}
.ddl-item.selected {
  background: #eef1f7;
  color: #2a345d;
  font-weight: 600;
}
.ddl-item-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ddl-item-meta {
  color: #98a0ad;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.ddl-item.selected .ddl-item-meta {
  color: #5b6577;
}
.ddl-empty {
  padding: 1rem 0.85rem;
  text-align: center;
  font-size: 0.85rem;
  color: #98a0ad;
}
</style>
